import axios from "axios";

const DEFAULT_SERVICE_WORKER_PATH = "/web-push-sw.js";
const DEFAULT_SERVICE_WORKER_SCOPE = "/";

function url_base64_to_uint8_array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    Reflect.set(outputArray, i, rawData.charCodeAt(i));
  }

  return outputArray;
}

function get_default_subscribe_url(baseUrl) {
  return `${baseUrl}/system_management/web_push/subscribe/`;
}

function get_default_unsubscribe_url(baseUrl) {
  return `${baseUrl}/system_management/web_push/unsubscribe/`;
}

function get_web_push_request_config(token) {
  return {
    headers: {
      Authorization: `Token ${token}`,
      "Content-Type": "application/json"
    }
  };
}

export function is_web_push_supported() {
  return (
    typeof window !== "undefined" &&
    window.isSecureContext &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function get_web_push_permission() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission;
}

export function get_vapid_public_key() {
  return process.env.VUE_APP_WEB_PUSH_PUBLIC_KEY || process.env.VUE_APP_VAPID_PUBLIC_KEY || "";
}

function wait_for_service_worker_state(worker, state) {
  if (!worker) return Promise.resolve();
  if (worker.state === state) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const handleStateChange = () => {
      if (worker.state === state) {
        worker.removeEventListener("statechange", handleStateChange);
        resolve();
      }

      if (worker.state === "redundant") {
        worker.removeEventListener("statechange", handleStateChange);
        reject(new Error("Web push service worker became redundant before it was active."));
      }
    };

    worker.addEventListener("statechange", handleStateChange);
  });
}

function is_push_service_registration_error(error) {
  return (
    error &&
    error.name === "AbortError" &&
    /push service/i.test(error.message || "")
  );
}

async function reset_web_push_service_worker_registration(registration) {
  if (registration) {
    await registration.unregister();
  }

  return get_active_web_push_service_worker_registration();
}

async function get_active_web_push_service_worker_registration() {
  const registration = await navigator.serviceWorker.register(DEFAULT_SERVICE_WORKER_PATH, {
    scope: DEFAULT_SERVICE_WORKER_SCOPE
  });

  if (registration.active) {
    return registration;
  }

  const installingWorker = registration.installing || registration.waiting;
  if (installingWorker) {
    await wait_for_service_worker_state(installingWorker, "activated");
  }

  return navigator.serviceWorker.ready;
}

export async function request_web_push_permission() {
  if (!get_vapid_public_key()) {
    throw new Error("Missing VUE_APP_WEB_PUSH_PUBLIC_KEY or VUE_APP_VAPID_PUBLIC_KEY.");
  }

  if (!is_web_push_supported()) {
    throw new Error("Web push is not supported by this browser.");
  }

  if (Notification.permission === "denied") {
    throw new Error("Browser notifications are blocked for this site.");
  }

  if (Notification.permission !== "granted") {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      throw new Error(
        permission === "denied"
          ? "Browser notifications are blocked for this site. Reset the site notification permission, then try again."
          : "Chrome did not show or complete the notification prompt. Check the permission icon near the address bar, then try again."
      );
    }
  }

  return Notification.permission;
}

export async function register_web_push_service_worker() {
  if (!is_web_push_supported()) {
    throw new Error("Web push is not supported by this browser.");
  }

  return get_active_web_push_service_worker_registration();
}

export async function get_existing_web_push_subscription() {
  if (!is_web_push_supported()) return null;

  const registration = await navigator.serviceWorker.getRegistration(DEFAULT_SERVICE_WORKER_SCOPE)
    || await register_web_push_service_worker();

  return registration.pushManager.getSubscription();
}

export async function subscribe_to_web_push({ baseUrl, token, userId, projectId, requestPermission = true }) {
  const vapidPublicKey = get_vapid_public_key();

  if (!vapidPublicKey) {
    throw new Error("Missing VUE_APP_WEB_PUSH_PUBLIC_KEY or VUE_APP_VAPID_PUBLIC_KEY.");
  }

  if (!is_web_push_supported()) {
    throw new Error("Web push is not supported by this browser.");
  }

  if (Notification.permission === "denied") {
    throw new Error("Browser notifications are blocked for this site.");
  }

  if (requestPermission && Notification.permission !== "granted") {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      throw new Error("Browser notification permission was not granted.");
    }
  }

  if (Notification.permission !== "granted") return null;

  const registration = await register_web_push_service_worker();
  let activeRegistration = registration;
  let subscription = await activeRegistration.pushManager.getSubscription();

  if (!subscription) {
    const subscriptionOptions = {
      userVisibleOnly: true,
      applicationServerKey: url_base64_to_uint8_array(vapidPublicKey)
    };

    try {
      subscription = await activeRegistration.pushManager.subscribe(subscriptionOptions);
    } catch (error) {
      if (!is_push_service_registration_error(error)) {
        throw error;
      }

      activeRegistration = await reset_web_push_service_worker_registration(activeRegistration);
      try {
        subscription = await activeRegistration.pushManager.subscribe(subscriptionOptions);
      } catch (retryError) {
        if (!is_push_service_registration_error(retryError)) {
          throw retryError;
        }

        throw new Error(
          "Browser push registration failed. Check that this site is opened over HTTPS or localhost and that the browser can reach its push service, then try again."
        );
      }
    }
  }

  const serializedSubscription = subscription.toJSON();

  await axios.post(
    process.env.VUE_APP_WEB_PUSH_SUBSCRIBE_URL || get_default_subscribe_url(baseUrl),
    {
      subscription: serializedSubscription,
      endpoint: serializedSubscription.endpoint,
      keys: serializedSubscription.keys,
      user_id: userId,
      project_id: projectId,
      user_agent: navigator.userAgent
    },
    get_web_push_request_config(token)
  );

  return subscription;
}

export async function unsubscribe_from_web_push({ baseUrl, token, userId }) {
  const subscription = await get_existing_web_push_subscription();

  if (!subscription) return;

  await axios.post(
    process.env.VUE_APP_WEB_PUSH_UNSUBSCRIBE_URL || get_default_unsubscribe_url(baseUrl),
    {
      endpoint: subscription.endpoint,
      user_id: userId
    },
    get_web_push_request_config(token)
  ).catch(error => console.warn("Failed to unregister web push subscription with backend.", error));

  await subscription.unsubscribe();
}

export async function sync_existing_web_push_subscription({ baseUrl, token, userId, projectId }) {
  if (!token || !userId || get_web_push_permission() !== "granted") return null;

  return subscribe_to_web_push({
    baseUrl,
    token,
    userId,
    projectId,
    requestPermission: false
  });
}
